import React from 'react';
import { ArcButton, ArcDialog, ArcMenuItem, ArcOutlinedInput, ArcOutlinedSelect, ArcSkeleton, ArcStack, ArcSwitch, ArcText } from '@src/core/ui';
import { SettingInputFieldProps, SettingPopupProps, SettingSelectFieldProps, SettingsGroupProps, SettingSwitcherFieldProps } from './types';
import { useSetSettingsMutation } from '@src/features/settings/redux';
import { UnitValueEnum } from '@src/features/settings/entities';
import { toast } from 'react-toastify';
import { EMPTY_VALUE } from '@src/core/config';
import { InputAdornment } from '@mui/material';
import { SettingItemEntity, SwitcherType, useGetSettingsQuery, useNumericRanges } from '@src/core/features/settings';
import { MeasurementType, SETTING_MEASUREMENT_MAP } from '@src/core/features/settings/entities';
import { NUMERIC_SETTINGS_KEYS } from '@src/features/settings/pages/settings-page/types';
import { droneSettingsKeysToExclude } from '@src/features/settings/config/settings-constants';
import { arcConvertBetweenUnitTypes, arcSpatiumUnit, arcToFixed, arcVelocitasUnit } from '@src/core/extensions';
import { getUnitType, useUnitType } from '@src/core/features/settings/units';
import { UnitType } from '@src/core/enums';
import { useNavigate } from 'react-router-dom';
import { routesConfig } from '@src/core/routes/routes-config';
import { trackError, toTrackedError } from '@src/core/sentry';

// Backend returns settings values in SI (m, m/s). Distance/speed settings are
// displayed and edited in the user's unit, so convert SI -> display for show/edit.
// On save the value stays in display unit until the setSettings mutation, which
// converts display -> SI and tags unitSystem=METRIC before POST (see settings/redux/api).
const settingMeasurement = (key: string): MeasurementType =>
  SETTING_MEASUREMENT_MAP[key] ?? MeasurementType.NONE;

const isConvertibleSetting = (key: string): boolean =>
  settingMeasurement(key) !== MeasurementType.NONE;

// Unit label for a distance/speed setting, computed from the user's unit (NOT the
// backend `unitValue`, which is now the SI label).
const settingUnitLabel = (setting: SettingItemEntity): string => {
  switch (settingMeasurement(setting.key)) {
    case MeasurementType.DISTANCE: return arcSpatiumUnit();
    case MeasurementType.SPEED:    return arcVelocitasUnit();
    default:                       return setting.unitValue;
  }
};

// SI -> display for a distance/speed setting value; non-convertible values pass through.
const settingDisplayValue = (setting: SettingItemEntity, raw: string): string => {
  const measurement = settingMeasurement(setting.key);
  if (measurement === MeasurementType.NONE) return raw;
  const num = Number(raw);
  if (!Number.isFinite(num)) return raw;
  const converted = arcConvertBetweenUnitTypes(num, measurement, UnitType.SI, getUnitType());
  return arcToFixed(converted, 1);
};


const Settings: React.FC = () => {
  const { data = { group: [] }, isLoading: isSettingsLoading } = useGetSettingsQuery();
  const [ setSettingItem ] = useSetSettingsMutation();
  const navigate = useNavigate();

  const onSetSettingItem = (settings: SettingItemEntity[]) => {
    return setSettingItem(settings).unwrap().then(() => {
      toast.success('SET SETTING SUCCESS TEXT');
    }).catch((err: unknown) => {
      trackError(toTrackedError(err), { code: 'FE_0018', extra: { action: 'setSettings' } });
      toast.error('Failed to save settings');
      throw err;
    });
  };

  if (isSettingsLoading) {
    return (
      <ArcStack spacing={2}>
        <ArcSkeleton height={50} />
        <ArcSkeleton height={50} />
        <ArcSkeleton height={50} />
        <ArcSkeleton height={50} />
        <ArcSkeleton height={50} />
        <ArcSkeleton height={50} />
      </ArcStack>
    );
  }

  return (
    <ArcStack spacing={2}>
      <ArcStack spacing={5}>
        {data.group.map((group) => {
          return (
            <SettingGroup 
              key={group.title}
              title={group.title} 
              settings={group.items} 
              isSubmitting={isSettingsLoading} 
              onSetSettingItem={onSetSettingItem} />
          );
        })}
      </ArcStack>
      <ArcStack spacing={1} direction='row' alignItems='center' justifyContent='space-between'>
        <ArcText.h5>Controller</ArcText.h5>
        <ArcText.h5
          sx={{ color: 'brand.main', cursor: 'pointer' }}
          onClick={() => navigate(routesConfig.settingsController)}
        >
          Configure
        </ArcText.h5>
      </ArcStack>
    </ArcStack>
  );
};


const SettingGroup: React.FC<SettingsGroupProps> = (props) => {
  const { title, settings, isSubmitting, onSetSettingItem } = props;
  return (
    <ArcStack spacing={2}>
      <ArcText.h3> {title} </ArcText.h3>
      <ArcStack spacing={1}>
        {settings.map((setting) => {
          if(droneSettingsKeysToExclude.includes(setting.key)) {
            return null;
          }
          if (setting.unitType === UnitValueEnum.DROPDOWN) {
            return (
              <SettingSelectField 
                key={`${setting.key}-${setting.value}`}
                setting={setting} 
                isSubmitting={isSubmitting} 
                onSetSettingItem={onSetSettingItem} />
            );
          }

          if (setting.unitType === UnitValueEnum.BOOLEAN) {
            return (
              <SettingSwitcherField 
                key={`${setting.key}-${setting.value}`}  
                setting={setting}
                isSubmitting={isSubmitting}  
                onSetSettingItem={onSetSettingItem} />
            );
          }

          return (
            <SettingItem 
              key={`${setting.key}-${setting.value}`}
              setting={setting} 
              isSubmitting={isSubmitting}
              onSetSettingItem={onSetSettingItem} />
          );
        })}
      </ArcStack>
    </ArcStack>
  );
};


const SettingSwitcherField: React.FC<SettingSwitcherFieldProps> = (props) => {
  const { setting, isSubmitting, onSetSettingItem } = props;

  return (
    <ArcStack spacing={1} direction='row' alignItems='center' justifyContent='space-between'>
      <ArcText.h5> {setting.name} </ArcText.h5>
      <ArcSwitch.brand 
        disabled={isSubmitting} 
        checked={setting.value == SwitcherType.ON}
        onChange={(_, checked) => {
          const payload = { ...setting, value: checked ? SwitcherType.ON : SwitcherType.OFF };
          onSetSettingItem([payload]);
        }}  />
    </ArcStack>
  );
};

const SettingSelectField: React.FC<SettingSelectFieldProps> = (props) => {
  const { setting, isSubmitting, onSetSettingItem } = props;

  return (
    <ArcStack spacing={1} direction='row' alignItems='center' justifyContent='space-between'>
      <ArcText.h5> {setting.name} </ArcText.h5>
      <ArcOutlinedSelect 
        sx={{ width: 250 }}
        value={setting.value}
        disabled={isSubmitting}
        onChange={async (e) => {
          const current = e.target.value;
          if (typeof current !== 'string') return;

          const { values, ...rest } = setting;
          const payload = { ...rest, value: current };
          await onSetSettingItem([payload]);
        }}>
        {(setting.values ?? []).map(({ value, name }) => (
          <ArcMenuItem 
            key={value} 
            value={value}> 
            {name} 
          </ArcMenuItem>
        ))}
      </ArcOutlinedSelect>
    </ArcStack>
  );
};

const SettingItem: React.FC<SettingInputFieldProps> = (props) => {

  const { setting, isSubmitting, onSetSettingItem } = props;
  // `value` is held in the user's DISPLAY unit. setting.value arrives in SI from the
  // backend, so convert SI -> display on load (and whenever the unit system changes).
  const unitType = useUnitType();
  const [value, setValue] = React.useState(() => settingDisplayValue(setting, setting.value ?? EMPTY_VALUE));
  const [showPopup, setShowPopup] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    setValue(settingDisplayValue(setting, setting.value ?? EMPTY_VALUE));
  }, [setting.value, setting.unitValue, setting.key, unitType]);

  const handleClose = () => {
    setShowPopup(false);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.blur();
      }
      if (document.activeElement && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }, 0);
  };

  const handleSaveFromPopup = (newValue: string) => {
    setValue(newValue);
    onSetSettingItem([{ ...setting, value: newValue }]);
    setShowPopup(false);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.blur();
      }
      if (document.activeElement && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }, 0);
  };

  return (
    <>
      <ArcStack spacing={1} direction='row' alignItems='center' justifyContent='space-between'>
        <ArcText.h5 onClick={() => setShowPopup(true)}> {setting.name} </ArcText.h5>
        <ArcOutlinedInput 
          ref={inputRef}
          value={arcToFixed(Number(value), 1)} 
          sx={{ width: 250 }} 
          aria-readonly={true}
          onClick={() => setShowPopup(true)}
          onFocus={(e) => e.target.style.cursor = 'pointer'}
          disabled={isSubmitting}
          slotProps={{ input: { readOnly: true, endAdornment: <InputAdornment position='end'> <ArcText.h5> {settingUnitLabel(setting)} </ArcText.h5> </InputAdornment> } }}
        />
      </ArcStack>
      <SettingPopup 
        open={showPopup}
        onClose={handleClose}
        onSave={handleSaveFromPopup} 
        setting={setting} 
        isSubmitting={isSubmitting} 
        currentValue={value}
      />
    </>
  );
};

const isNumericSetting = (setting: SettingItemEntity): boolean => {
  return NUMERIC_SETTINGS_KEYS.includes(setting.key);
};

const SettingPopup: React.FC<SettingPopupProps> = (props) => {
  const { setting, isSubmitting, onSave, open, onClose, currentValue } = props;

  const isNumeric = isNumericSetting(setting);
  const numericRanges = useNumericRanges();

  const [displayValue, setDisplayValue] = React.useState<string>("");

  React.useEffect(() => {
    // currentValue is already in the user's display unit (converted by SettingItem).
    // The setting.value fallback is SI, so convert it before editing.
    const initial = currentValue ?? settingDisplayValue(setting, setting.value ?? EMPTY_VALUE);

    if (isNumeric && initial !== "" && !isNaN(Number(initial))) {
      setDisplayValue(arcToFixed(Number(initial), 1));
    } else {
      setDisplayValue(String(initial));
    }
  }, [currentValue, setting.value, open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;

    if (isNumeric) {
      if (/^\d*\.?\d{0,1}$/.test(v) || v === "") {
        setDisplayValue(v);
      }
    } else {
      setDisplayValue(v);
    }
  };

  const handleSave = () => {
    if (isNumeric) {
      onSave(arcToFixed(Number(displayValue || 0), 1));
    } else {
      onSave(displayValue);
    }
  };

  return (
    <ArcDialog
      open={open ?? false}
      onClose={onClose}
      title={`Change ${setting.name}`}
      contextSX={{ px: 4, paddingBottom: 3 }}
    >
      <ArcStack spacing={2}>
        <ArcOutlinedInput
          autoComplete="off"
          label={setting.name}
          value={displayValue}
          onChange={handleChange}
          minNumber={numericRanges[setting.key]?.min}
          maxNumber={numericRanges[setting.key]?.max}
          inputProps={
            isNumeric
              ? {
                pattern: "^\\d*(\\.\\d?)?$",
                inputMode: "decimal",
              }
              : {}
          }
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <ArcText.h5>{settingUnitLabel(setting)}</ArcText.h5>
                </InputAdornment>
              ),
            },
          }}
        />

        <ArcButton.brand onClick={handleSave} isLoading={isSubmitting}>
          Save
        </ArcButton.brand>
      </ArcStack>
    </ArcDialog>
  );
};


export default Settings;